import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MdDashboard,
  MdReceipt,
  MdNotifications,
  MdChevronLeft,
  MdChevronRight,
  MdExtension,
  MdCalendarMonth,
  MdClose,
  MdLightbulb,
} from 'react-icons/md';
import type { MenuItem } from './types';
import SidebarMenuItem from './SidebarMenuItem';
import SidebarUser from './SidebarUser';
import SidebarMessages from './SidebarMessages';
import SidebarCTA from './SidebarCTA';

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  // Menu items configuration
  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <MdDashboard size={24} />,
    },
    {
      id: 'Campaign Ideas',
      label: 'Campaign Ideas',
      icon: <MdLightbulb size={24} />,
    },
    {
      id: 'Marketing Calendar',
      label: 'Marketing Calendar',
      icon: <MdCalendarMonth size={24} />,
    },
    
    {
      id: 'Market Intelligence',
      label: 'Market Intelligence',
      icon: <MdReceipt size={24} />,
      submenu: [
        { id: 'Demographics', label: 'Demographics' },
        { id: 'Menu Enhancement', label: 'Menu Enhancement' },
        { id: 'Competitor', label: 'Competitor' },
        { id: 'Review Analysis', label: 'Review Analysis' },
        { id: 'Competitors Promotion', label: 'Competitors Promotion' },
      ],
    },
    {
      id: 'Integrations',
      label: 'Integrations',
      icon: <MdExtension size={24} />,
    },
  ];

  // Sample message users
  const messageUsers = [
    {
      id: 1,
      name: 'Erik Gunsel',
      avatar: 'https://i.pravatar.cc/150?img=12',
      online: true,
    },
    {
      id: 2,
      name: 'Emily Smith',
      avatar: 'https://i.pravatar.cc/150?img=45',
      online: true,
    },
    {
      id: 3,
      name: 'Arthur Adelk',
      avatar: 'https://i.pravatar.cc/150?img=33',
      online: true,
    },
  ];

  const toggleSubmenu = (id: string, hasSubmenu: boolean) => {
    // If sidebar is collapsed and item has submenu, expand the sidebar first
    if (isCollapsed && hasSubmenu) {
      setIsCollapsed(false);
      setOpenSubmenu(id);
    } else {
      setOpenSubmenu(openSubmenu === id ? null : id);
    }
  };

  return (
    <motion.div
      initial={false}
      animate={{
        width: isCollapsed ? '80px' : '280px',
      }}
      transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      }}
      className="relative h-screen bg-[#212121]/95 backdrop-blur-xl border-r border-[#616161]/20 flex flex-col"
      style={{
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
      }}
    >

      {/* User Profile */}
      <SidebarUser isCollapsed={isCollapsed} />

      {/* Main Menu Section */}
      <div className="flex-1 overflow-y-auto mt-5 overflow-x-hidden px-4 pb-4">
        {/* MAIN Section */}
        <div className="mb-6">
        
          <div className="space-y-1">
            {menuItems.map((item) => (
              <SidebarMenuItem
                key={item.id}
                item={item}
                isCollapsed={isCollapsed}
                isOpen={openSubmenu === item.id}
                onToggle={() => toggleSubmenu(item.id, !!(item.submenu && item.submenu.length > 0))}
              />
            ))}
          </div>
        </div>

        {/* MESSAGES Section */}
        {/* <SidebarMessages
          users={messageUsers}
          isCollapsed={isCollapsed}
        /> */}
      </div>

      {/* Call to Action Card */}
      <SidebarCTA isCollapsed={isCollapsed} />

      {/* Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-1 top-[53px] cursor-pointer w-7 h-7 bg-[#EE4423] hover:bg-[#d63d20] rounded-full flex items-center justify-center transition-all duration-300 shadow-lg z-50 overflow-visible"
        style={{ marginRight: '4px' }}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isCollapsed ? 0 : 180 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          {isCollapsed ? (
            <MdChevronRight size={18} className="text-white" />
          ) : (
            <MdClose size={18} className="text-white" />
          )}
        </motion.div>
      </button>
    </motion.div>
  );
};

export default Sidebar;
